import { ReactNode, useState } from "react";
import styled from "styled-components";

import { ErrorComponent } from "@components/ErrorComponent";
import { AppNav } from "@components/Header/AppNav";
import { Loader } from "@components/Loader";
import { Overlay } from "@components/Overlay";
import { Body, BodySmall } from "@components/Typography";
import { SUPPORT_EMAIL_URL } from "@constants";
import { BillingInterval, PlanOption, SubscriptionPlan } from "@ctypes";
import {
  useErrorBanner,
  useModal,
  useSubscription,
  useTheme,
  useUpdateSubscription,
  useUser,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import {
  Card,
  Content,
  Eyebrow,
  FilledButton,
  GhostButton,
  Grain,
  Page,
  SerifTitle,
} from "@routes/DashboardRoute/Home/styles";
import { home, homeCssVars } from "@routes/DashboardRoute/Home/tokens";
import { useStyles } from "./styles";
import { SubscriptionData } from "./types";

const Hero = styled.div`
  padding: 28px ${home.pagePad} 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 640px;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: ${home.ink58};
`;

const PlansGrid = styled.div`
  padding: 30px ${home.pagePad} 72px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  align-items: stretch;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const PlanCard = styled(Card)<{ $current: boolean }>`
  padding: 28px 26px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  border-color: ${({ $current }) => ($current ? "rgba(143,191,176,0.34)" : home.line10)};
  ${({ $current }) =>
    $current &&
    `background: linear-gradient(155deg, rgba(143,191,176,0.14) 0%, rgba(20,26,40,0.72) 66%);`}
`;

const PlanName = styled.div`
  font-family: ${home.serif};
  font-size: 26px;
  letter-spacing: -0.3px;
`;

const CurrentBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 11px 0 8px;
  border-radius: 999px;
  background: rgba(143, 191, 176, 0.26);
  font-size: 11px;
  font-weight: 600;
  color: #b7dcce;
  margin-left: auto;
  flex-shrink: 0;
`;

const PriceLine = styled.div`
  font-size: 15px;
  color: ${home.ink};

  b {
    font-size: 27px;
    font-weight: 600;
    letter-spacing: -0.8px;
  }

  .alt {
    font-size: 13px;
    color: ${home.ink52};
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;

  .row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .check {
    width: 17px;
    height: 17px;
    flex-shrink: 0;
    margin-top: 1px;
    border-radius: 999px;
    background: rgba(143, 191, 176, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${home.sage};
  }

  .text {
    font-size: 13px;
    line-height: 1.5;
    color: ${home.ink66};
  }
`;

const ProgressBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${home.fill06};
  border: 1px solid ${home.line10};

  .title {
    font-size: 13px;
    font-weight: 500;
    color: ${home.ink};
  }

  .rail {
    height: 6px;
    border-radius: 999px;
    background: ${home.fill10};
    overflow: hidden;
  }

  .fill {
    height: 100%;
    border-radius: 999px;
    background: ${home.copper};
    transition: width 240ms ease;
  }

  .description {
    font-size: 12px;
    line-height: 1.5;
    color: ${home.ink52};
  }
`;

const CardActions = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;

  button {
    width: 100%;
    justify-content: center;
  }
`;

export const SubscriptionRoute = () => {
  const { theme } = useTheme();
  const { data, loading, error } = useSubscription();

  return (
    <Page style={homeCssVars(theme.meta.dark)}>
      <Grain aria-hidden="true" />
      <Content>
        <AppNav />

        {loading ? (
          <Hero>
            <Loader />
          </Hero>
        ) : error || !data ? (
          <Hero>
            <ErrorComponent />
          </Hero>
        ) : (
          <SubscriptionContent subscription={data} />
        )}
      </Content>
    </Page>
  );
};

const SubscriptionContent = ({ subscription }: { subscription: SubscriptionData }) => {
  const { show } = useModal();
  const { isIntegratedPartner } = useUser();

  const currentPlan = subscription.plan;

  const actionFor = (plan: SubscriptionPlan): ReactNode => {
    if (isIntegratedPartner) return null;

    const isCurrent = plan === currentPlan;

    /* -------- current-plan cards -------- */
    if (isCurrent) {
      if (plan === SubscriptionPlan.Starter) return null;
      if (subscription.hasReachedAssetThreshold) return null;

      return (
        <GhostButton onClick={() => show(<PaymentModal plan={plan} subscription={subscription} />)}>
          {i18n.t(Translations.SubscriptionRouteManagePlan)}
        </GhostButton>
      );
    }

    /* -------- switching cards -------- */
    if (plan === SubscriptionPlan.Starter) {
      // Threshold reached: Starter is no longer available.
      if (subscription.hasReachedAssetThreshold) return null;

      if (currentPlan === SubscriptionPlan.Generations) {
        return (
          <GhostButton
            onClick={() => {
              window.location.href = SUPPORT_EMAIL_URL;
            }}
          >
            {i18n.t(Translations.SubscriptionRouteReturnToStarter)}
          </GhostButton>
        );
      }

      return (
        <GhostButton onClick={() => show(<DowngradeModal />)}>
          {i18n.t(Translations.SubscriptionRouteReturnToStarter)}
        </GhostButton>
      );
    }

    if (plan === SubscriptionPlan.Pro) {
      if (currentPlan === SubscriptionPlan.Generations) {
        return (
          <GhostButton
            onClick={() => {
              window.location.href = SUPPORT_EMAIL_URL;
            }}
          >
            {i18n.t(Translations.SubscriptionRouteDowngradeToPro)}
          </GhostButton>
        );
      }

      if (subscription.hasReachedAssetThreshold) return null;

      return (
        <FilledButton
          onClick={() => show(<PaymentModal plan={plan} subscription={subscription} />)}
        >
          {i18n.t(Translations.SubscriptionRouteUpgradeToPro)}
        </FilledButton>
      );
    }

    return (
      <FilledButton onClick={() => show(<PaymentModal plan={plan} subscription={subscription} />)}>
        {i18n.t(Translations.SubscriptionRouteUpgradeToGenerations)}
      </FilledButton>
    );
  };

  return (
    <>
      <Hero>
        <Eyebrow>{i18n.t(Translations.SubscriptionRouteEyebrow)}</Eyebrow>
        <SerifTitle as="h1" $size={46} style={{ margin: 0 }}>
          {i18n.t(Translations.SubscriptionRouteTitleLead)}{" "}
          <em>{i18n.t(Translations.SubscriptionRouteTitleHighlight)}</em>.
        </SerifTitle>
        <Subtitle>{i18n.t(Translations.SubscriptionRouteSubtitle)}</Subtitle>
      </Hero>

      <PlansGrid>
        {(
          [SubscriptionPlan.Starter, SubscriptionPlan.Pro, SubscriptionPlan.Generations] as const
        ).map((plan) => (
          <PlanPanel key={plan} plan={plan} subscription={subscription} action={actionFor(plan)} />
        ))}
      </PlansGrid>
    </>
  );
};

const PlanPanel = ({
  plan,
  subscription,
  action,
}: {
  plan: SubscriptionPlan;
  subscription: SubscriptionData;
  action: ReactNode;
}) => {
  const { isIntegratedPartner } = useUser();

  const currentPlan = subscription.plan;
  const isCurrent = plan === currentPlan;

  const title = (() => {
    if (plan === SubscriptionPlan.Starter) return i18n.t(Translations.StarterPlanTitle);
    if (plan === SubscriptionPlan.Pro) return i18n.t(Translations.ProPlanTitle);
    return i18n.t(Translations.GenerationsPlanTitle);
  })();

  const items = (() => {
    if (plan === SubscriptionPlan.Starter) {
      return isIntegratedPartner
        ? [Translations.StarterPlanPartnerItem1, Translations.StarterPlanPartnerItem2]
        : [
            Translations.StarterPlanItem1,
            Translations.StarterPlanItem2,
            Translations.StarterPlanItem3,
          ];
    }

    if (plan === SubscriptionPlan.Pro) {
      if (isIntegratedPartner || subscription.hasReachedAssetThreshold) {
        return [Translations.ProPlanPartnerItem1, Translations.ProPlanPartnerItem2];
      }
      return [Translations.ProPlanItem1, Translations.ProPlanItem2, Translations.ProPlanItem3];
    }

    return [
      Translations.GenerationsPlanItem1,
      Translations.GenerationsPlanItem2,
      Translations.GenerationsPlanItem3,
      Translations.GenerationsPlanItem4,
    ];
  })();

  const price = (() => {
    if (plan === SubscriptionPlan.Starter) {
      return (
        <PriceLine>
          <b>{i18n.t(Translations.SubscriptionRouteFree)}</b>{" "}
          <span className="alt">{i18n.t(Translations.StarterPlanSubtitle)}</span>
        </PriceLine>
      );
    }

    const monthly =
      plan === SubscriptionPlan.Pro
        ? i18n.t(Translations.ProPlanMonthly)
        : i18n.t(Translations.GenerationsPlanMonthly);
    const annually =
      plan === SubscriptionPlan.Pro
        ? i18n.t(Translations.ProPlanAnnually)
        : i18n.t(Translations.GenerationsPlanAnnually);

    // An active subscriber sees the interval they are actually billed on.
    if (isCurrent) {
      return (
        <PriceLine>
          <b>{subscription.billingInterval === BillingInterval.Annually ? annually : monthly}</b>
        </PriceLine>
      );
    }

    return (
      <PriceLine>
        <b>{monthly}</b>{" "}
        <span className="alt">
          {i18n.t(Translations.PlanPriceOr)}
          {annually}
        </span>
      </PriceLine>
    );
  })();

  const progress = (() => {
    if (plan === SubscriptionPlan.Starter) return null;

    const isProgressActive =
      plan === SubscriptionPlan.Pro
        ? (!subscription.hasReachedAssetThreshold && currentPlan === SubscriptionPlan.Starter) ||
          subscription.hasReachedAssetThreshold
        : (!subscription.hasReachedAssetThreshold &&
            currentPlan !== SubscriptionPlan.Generations) ||
          subscription.hasReachedAssetThreshold;

    if (!isProgressActive) return null;

    const titleKey =
      plan === SubscriptionPlan.Pro
        ? isCurrent
          ? Translations.ProPlanActiveTitle
          : Translations.ProPlanProgressTitle
        : isCurrent
          ? Translations.GenerationsPlanActiveTitle
          : Translations.GenerationsPlanProgressTitle;

    const descriptionKey =
      plan === SubscriptionPlan.Pro
        ? isCurrent
          ? Translations.ProPlanActiveDescription
          : Translations.ProPlanProgressDescription
        : isCurrent
          ? Translations.GenerationsPlanActiveDescription
          : Translations.GenerationsPlanProgressDescription;

    const percent = Math.min(Math.max(subscription.assetThresholdPercent, 0), 100);

    return (
      <ProgressBlock>
        <div className="title">{i18n.t(titleKey)}</div>
        <div className="rail">
          <div className="fill" style={{ width: `${percent}%` }} />
        </div>
        <div className="description">{i18n.t(descriptionKey)}</div>
      </ProgressBlock>
    );
  })();

  return (
    <PlanCard $current={isCurrent}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <PlanName>{title}</PlanName>
        {isCurrent && (
          <CurrentBadge>
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m5 13 4 4L19 7" />
            </svg>
            {i18n.t(Translations.SubscriptionRouteCurrentPlan)}
          </CurrentBadge>
        )}
      </div>

      {price}

      {progress}

      <FeatureList>
        {items.map((key) => (
          <div key={key} className="row">
            <span className="check">
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m5 13 4 4L19 7" />
              </svg>
            </span>
            <span className="text">{i18n.t(key)}</span>
          </div>
        ))}
      </FeatureList>

      {action && <CardActions>{action}</CardActions>}
    </PlanCard>
  );
};

/* ---------- modals ---------- */

const PaymentModal = ({
  plan,
  subscription,
}: {
  plan: SubscriptionPlan;
  subscription: SubscriptionData;
}) => {
  const styles = useStyles();
  const { show, dismiss } = useModal();
  const { showErrorBanner } = useErrorBanner();

  const isGenerations = plan === SubscriptionPlan.Generations;

  const monthlyOption = isGenerations ? PlanOption.GenerationsMonthly : PlanOption.ProMonthly;
  const annualOption = isGenerations ? PlanOption.GenerationsAnnually : PlanOption.ProAnnually;

  const currentOption = (() => {
    if (subscription.plan !== plan) return null;
    if (subscription.billingInterval === BillingInterval.Monthly) return monthlyOption;
    if (subscription.billingInterval === BillingInterval.Annually) return annualOption;
    return null;
  })();

  const [planOption, setPlanOption] = useState<PlanOption>(monthlyOption);

  const [updateSubscription, { loading }] = useUpdateSubscription();

  const handleSubmit = async () => {
    try {
      await updateSubscription({ variables: { planOption } });

      show(
        <Overlay
          title={i18n.t(
            isGenerations
              ? Translations.SubscriptionRouteGenerationsConfirmedTitle
              : Translations.SubscriptionRouteProConfirmedTitle,
          )}
          cta={{
            primary: {
              title: i18n.t(Translations.SubscriptionRouteConfirmedCTA),
              onClick: dismiss,
            },
          }}
        />,
      );
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
      dismiss();
    }
  };

  const options = [
    {
      option: monthlyOption,
      title: i18n.t(Translations.PlanPaymentMonthlyTitle),
      subtitle: i18n.t(
        isGenerations ? Translations.GenerationsPlanMonthly : Translations.ProPlanMonthly,
      ),
      discount: null as string | null,
    },
    {
      option: annualOption,
      title: i18n.t(Translations.PlanPaymentAnnualTitle),
      subtitle: i18n.t(
        isGenerations ? Translations.GenerationsPlanAnnually : Translations.ProPlanAnnually,
      ),
      discount: i18n.t(Translations.PlanPaymentDiscount),
    },
  ];

  return (
    <Overlay
      title={i18n.t(isGenerations ? Translations.GenerationsPlanTitle : Translations.ProPlanTitle)}
      cta={{
        primary: {
          title: i18n.t(
            currentOption
              ? Translations.SubscriptionRouteSwitchPlan
              : Translations.SubscriptionRoutePaymentCTA,
          ),
          onClick: handleSubmit,
          loading,
          disabled: planOption === currentOption,
        },
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {options.map((item) => (
          <button
            key={item.option}
            type="button"
            style={{
              ...styles.modalOption,
              ...(planOption === item.option ? styles.modalOptionSelected : {}),
            }}
            onClick={() => setPlanOption(item.option)}
          >
            <div>
              <Body>{item.title}</Body>
              <BodySmall style={styles.modalOptionSubtitle}>{item.subtitle}</BodySmall>
            </div>
            {item.discount && <span style={styles.discount}>{item.discount}</span>}
          </button>
        ))}

        <BodySmall style={styles.modalFooter}>{i18n.t(Translations.PlanPaymentFooter)}</BodySmall>
      </div>
    </Overlay>
  );
};

const DowngradeModal = () => {
  const { show, dismiss } = useModal();
  const { showErrorBanner } = useErrorBanner();

  const [updateSubscription, { loading }] = useUpdateSubscription();

  const handleDowngrade = async () => {
    try {
      await updateSubscription({
        variables: { planOption: PlanOption.Starter },
      });

      show(
        <Overlay
          title={i18n.t(Translations.SubscriptionRouteStarterConfirmedTitle)}
          cta={{
            primary: {
              title: i18n.t(Translations.SubscriptionRouteConfirmedCTA),
              onClick: dismiss,
            },
          }}
        />,
      );
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
      dismiss();
    }
  };

  return (
    <Overlay
      title={i18n.t(Translations.SubscriptionRouteDowngradeTitle)}
      subtitle={i18n.t(Translations.SubscriptionRouteDowngradeSubtitle)}
      cta={{
        primary: {
          title: i18n.t(Translations.SubscriptionRouteReturnToStarter),
          onClick: handleDowngrade,
          loading,
        },
        secondary: {
          title: i18n.t(Translations.SubscriptionRouteDowngradeDismiss),
          onClick: dismiss,
        },
      }}
    />
  );
};
